import React from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  ArrowLeft, Brain, Play, Target, TrendingUp,
  Clock, ChevronRight, Star, Zap, Puzzle
} from "lucide-react";

/**
 * Banking Reasoning Ability Practice Hub
 */
const BankingReasoningPage = () => {
  const navigate = useNavigate();

  const topics = [
    {
      id: 'seating-linear',
      name: 'Linear Seating Arrangement',
      description: 'Single & double row arrangements',
      questions: 100,
      practiced: 0,
      difficulty: 'High',
      icon: '🪑',
      color: 'bg-purple-500'
    },
    {
      id: 'seating-circular',
      name: 'Circular Seating',
      description: 'Round table arrangements',
      questions: 80,
      practiced: 0,
      difficulty: 'High',
      icon: '⭕',
      color: 'bg-violet-500'
    },
    {
      id: 'puzzles',
      name: 'Puzzles',
      description: 'Floor, box, scheduling puzzles',
      questions: 150,
      practiced: 0,
      difficulty: 'High',
      icon: '🧩',
      color: 'bg-indigo-500'
    },
    {
      id: 'syllogism',
      name: 'Syllogism',
      description: 'All, some, no, only statements',
      questions: 80,
      practiced: 0,
      difficulty: 'Medium',
      icon: '📝',
      color: 'bg-blue-500'
    },
    {
      id: 'blood-relations',
      name: 'Blood Relations',
      description: 'Family tree problems',
      questions: 60,
      practiced: 0,
      difficulty: 'Medium',
      icon: '👨‍👩‍👧‍👦',
      color: 'bg-pink-500'
    },
    {
      id: 'direction',
      name: 'Direction Sense',
      description: 'North, South, East, West',
      questions: 50,
      practiced: 0,
      difficulty: 'Easy',
      icon: '🧭',
      color: 'bg-emerald-500'
    },
    {
      id: 'coding-decoding',
      name: 'Coding-Decoding',
      description: 'Pattern based coding',
      questions: 70,
      practiced: 0,
      difficulty: 'Medium',
      icon: '🔐',
      color: 'bg-amber-500'
    },
    {
      id: 'inequality',
      name: 'Inequality',
      description: 'Symbol based comparisons',
      questions: 60,
      practiced: 0,
      difficulty: 'Easy',
      icon: '⚖️',
      color: 'bg-teal-500'
    },
    {
      id: 'input-output',
      name: 'Input-Output',
      description: 'Machine input patterns',
      questions: 50,
      practiced: 0,
      difficulty: 'High',
      icon: '⚙️',
      color: 'bg-rose-500'
    },
    {
      id: 'data-sufficiency',
      name: 'Data Sufficiency',
      description: 'Is the data enough?',
      questions: 40,
      practiced: 0,
      difficulty: 'Medium',
      icon: '❓',
      color: 'bg-cyan-500'
    }
  ];

  const getDifficultyColor = (diff) => {
    switch(diff) {
      case 'Easy': return 'bg-emerald-100 text-emerald-700';
      case 'Medium': return 'bg-amber-100 text-amber-700';
      case 'High': return 'bg-red-100 text-red-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-purple-50/30 pb-20">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 via-violet-600 to-purple-700 text-white">
        <div className="max-w-6xl mx-auto px-4 py-6">
          <div className="flex items-center gap-4 mb-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/banking')}
              className="bg-white/10 hover:bg-white/20 text-white"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                <Brain className="w-6 h-6" />
              </div>
              <div>
                <h1 className="text-xl font-bold">Reasoning Ability</h1>
                <p className="text-purple-200 text-sm">35 Marks in Prelims | 60 Marks in Mains</p>
              </div>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-4 gap-4">
            <div className="bg-white/10 rounded-xl p-3 text-center">
              <p className="text-2xl font-bold">0</p>
              <p className="text-xs text-purple-200">Questions Done</p>
            </div>
            <div className="bg-white/10 rounded-xl p-3 text-center">
              <p className="text-2xl font-bold">0%</p>
              <p className="text-xs text-purple-200">Accuracy</p>
            </div>
            <div className="bg-white/10 rounded-xl p-3 text-center">
              <p className="text-2xl font-bold">0</p>
              <p className="text-xs text-purple-200">Puzzles Solved</p>
            </div>
            <div className="bg-white/10 rounded-xl p-3 text-center">
              <p className="text-2xl font-bold">10</p>
              <p className="text-xs text-purple-200">Topics</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 py-6 space-y-6">
        
        {/* Quick Practice */}
        <Card className="bg-gradient-to-br from-slate-800 to-slate-900 text-white border-0">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-bold text-lg flex items-center gap-2">
                  <Puzzle className="w-5 h-5 text-purple-400" />
                  Daily Puzzle Set
                </h3>
                <p className="text-slate-400 text-sm">5 puzzles + 15 mixed reasoning questions</p>
              </div>
              <Button 
                className="bg-purple-500 hover:bg-purple-600 text-white"
                onClick={() => navigate('/banking/reasoning/practice?section=reasoning&mode=daily')}
              >
                <Play className="w-4 h-4 mr-2" /> Start Practice
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Topics Grid */}
        <div>
          <h2 className="text-lg font-bold text-slate-900 mb-4">Practice by Topic</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            {topics.map((topic) => (
              <Card
                key={topic.id}
                className="cursor-pointer hover:shadow-lg transition-all hover:scale-[1.02] border-2 border-transparent hover:border-purple-200"
                onClick={() => navigate(`/banking/reasoning/practice?section=reasoning&topic=${topic.id}`)}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className={`w-10 h-10 ${topic.color} rounded-lg flex items-center justify-center`}>
                      <span className="text-xl">{topic.icon}</span>
                    </div>
                    <Badge className={getDifficultyColor(topic.difficulty)}>
                      {topic.difficulty}
                    </Badge>
                  </div>
                  <h3 className="font-bold text-slate-900 text-sm mb-1">{topic.name}</h3>
                  <p className="text-xs text-slate-500 mb-3">{topic.description}</p>
                  <div className="flex items-center justify-between text-xs text-slate-400">
                    <span>{topic.practiced}/{topic.questions}</span>
                    <span>{Math.round((topic.practiced/topic.questions)*100)}%</span>
                  </div>
                  <Progress value={(topic.practiced/topic.questions)*100} className="h-1.5 mt-2" />
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Tips Section */}
        <Card className="bg-purple-50 border-purple-200">
          <CardContent className="p-4">
            <h3 className="font-bold text-purple-800 mb-2 flex items-center gap-2">
              <Star className="w-5 h-5" /> Reasoning Tips for Banking Exams
            </h3>
            <ul className="text-sm text-purple-700 space-y-1">
              <li>- Puzzles & Seating = 15-20 marks. Master them first!</li>
              <li>- Always draw diagrams for seating arrangements</li>
              <li>- Learn Syllogism rules - guaranteed 5 marks</li>
              <li>- Don't spend more than 8-10 mins on a single puzzle set</li>
              <li>- Practice Input-Output patterns daily - 5 easy marks if you know the pattern</li>
            </ul>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default BankingReasoningPage;
